import { useMemo, useSyncExternalStore } from 'react';
import {
  getCellWidths,
  getColumnByType,
  getRowCells,
  getRowHeight,
  updateDataByType
} from '../utils/functions';
import commonStore from '../../../../store/common';
import { CustomHeaderWithDrillDown } from '../../components/CustomHeaderWithDrillDown';
import reportStore from '../../store';
import { reaction } from 'mobx';
import { useSearchParams } from 'react-router-dom';
import { isEsmaTableDisabledForFund } from '../utils/visibleSections';

export default function useTable({
  data,
  onChangeFund,
  schema,
  isStaticTemplate,
  requiredFields,
  funds,
  isMultiFunds,
  tableName,
  columnLables
}) {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const currentDataKey =
    type === 'AIF001' ? 'AIFOO1' : searchParams.get('fund_id') || data?.[0]?.fund_id;
  const isTableDisabledForFund = fundOrId => {
    const fund =
      typeof fundOrId === 'object' && fundOrId !== null
        ? fundOrId
        : funds[String(fundOrId)] || funds[fundOrId];
    return isEsmaTableDisabledForFund(fund, tableName);
  };

  const isComparisonReport = useSyncExternalStore(
    subscribe => {
      const dispose = reaction(() => reportStore.isComparisonReport, subscribe, {
        fireImmediately: false
      });
      return dispose;
    },
    () => reportStore.isComparisonReport
  );

  const onChange = value => {
    const changes = value[0];
    const fund = isMultiFunds
      ? JSON.parse(JSON.stringify(funds[changes.newCell.fundId].fund_data[tableName]))
      : JSON.parse(JSON.stringify(data));
    fund[changes.newCell.dataIndex][changes.newCell.dataKey] = updateDataByType({
      cell: changes.newCell
    });
    onChangeFund(tableName, fund, changes.newCell.fundId);
  };

  const contentWidth = useSyncExternalStore(
    subscribe => reaction(() => commonStore.contentWidth, subscribe, { fireImmediately: false }),
    () => commonStore.contentWidth
  );

  const rows = useMemo(() => {
    if (!data) return [];

    const { labelWidth, valueWidth } = getCellWidths({
      isMultiFunds,
      fundSize: Object.keys(funds).length,
      contentWidth,
      isComparisonReport
    });

    let _rows = [];

    if (isComparisonReport) {
      if (isMultiFunds) {
        _rows = [
          {
            rowId: 0,
            height: 40,
            cells: [
              {
                type: 'header',
                text: ''
              },
              {
                type: 'header',
                text: ''
              },
              ...Object.values(funds)
                .map(item => [item, item.previous_fund])
                .flat()
                .map((item, index) => ({
                  type: 'header',
                  text: item?.fund_name || '',
                  colspan: index % 2 === 0 ? 2 : undefined
                }))
            ]
          },
          {
            rowId: 1,
            height: 40,
            cells: [
              {
                type: 'header',
                text: ''
              },
              {
                type: 'header',
                text: ''
              },
              ...Object.values(funds)
                .map(item => [item.previous_fund, item])
                .flat()
                .map(item => ({
                  type: 'header',
                  text: `${item.period}, ${item.year}`
                }))
            ]
          }
        ];
      } else {
        _rows = [
          {
            rowId: 0,
            height: 40,
            cells: [
              {
                type: 'header',
                text: ''
              },
              {
                type: 'header',
                text: ''
              },
              {
                type: 'header',
                text: `${funds[currentDataKey].previous_fund?.period}, ${funds[currentDataKey].previous_fund?.year}`
              },
              {
                type: 'header',
                text: `${funds[currentDataKey].period}, ${funds[currentDataKey].year}`
              }
            ]
          }
        ];
      }
    } else {
      if (isMultiFunds) {
        _rows = [
          {
            rowId: 0,
            height: 40,
            cells: [
              {
                type: 'header',
                text: ''
              },
              {
                type: 'header',
                text: ''
              },
              ...Object.values(funds).map(item => ({
                type: 'header',
                text: item?.fund_name || ''
              }))
            ]
          }
        ];
      }
    }

    return [
      ..._rows,
      ...Object.keys(columnLables).map((key, index) => {
        let _cells = isMultiFunds
          ? isComparisonReport
            ? Object.values(funds)
                .map(item => [item.previous_fund, item])
                .flat()
                .map((value, j) => {
                  return getColumnByType({
                    type: schema?.types?.[key]?.type,
                    options: schema?.types?.[key]?.options,
                    key,
                    defaultValue: schema?.defaultValues?.[key],
                    index: 0,
                    value: value.fund_data[tableName][0],
                    required:
                      funds[value.fund_id]?.required_tables?.[tableName]?.field_names?.includes(
                        key
                      ),
                    tableName: tableName,
                    isStaticTemplate,
                    fundId: value.fund_id,
                    isSectionDisabled: isTableDisabledForFund(value.fund_id),
                    fundName: value.fund_name,
                    hasDrillDownFeature:
                      !!value.fund_data[tableName][0]?.report_id &&
                      value.fund_data[tableName][0]?.[key] !== null &&
                      schema.types[key].has_drill_down_feature,
                    reportId: value.fund_data[tableName][0]?.report_id,
                    tableId: value.fund_data[tableName][0]?.id,
                    title: columnLables[key]?.name,
                    nonEditable: columnLables[key].disabled || isComparisonReport,
                    isMultiFunds,
                    isEsma: true,
                    warning: value.fund_data[tableName][0]?.warning?.[key],
                    fieldOrder: columnLables[key]?.order,
                    difference:
                      j % 2 === 1
                        ? funds[value.fund_id]?.difference?.[tableName]?.[0]?.[key] || {}
                        : null
                  });
                })
            : Object.values(funds).map(value =>
                getColumnByType({
                  type: schema?.types?.[key]?.type,
                  options: schema?.types?.[key]?.options,
                  key,
                  defaultValue: schema?.defaultValues?.[key],
                  index: 0,
                  value: value.fund_data[tableName][0],
                  required:
                    funds[value.fund_id]?.required_tables?.[tableName]?.field_names?.includes(key),
                  tableName: tableName,
                  isStaticTemplate,
                  fundId: value.fund_id,
                  isSectionDisabled: isTableDisabledForFund(value.fund_id),
                  fundName: value.fund_name,
                  hasDrillDownFeature:
                    !!value.fund_data[tableName][0]?.report_id &&
                    value.fund_data[tableName][0]?.[key] !== null &&
                    schema.types[key].has_drill_down_feature,
                  reportId: value.fund_data[tableName][0]?.report_id,
                  tableId: value.fund_data[tableName][0]?.id,
                  title: columnLables[key]?.name,
                  nonEditable: columnLables[key].disabled || isComparisonReport,
                  isMultiFunds,
                  isEsma: true,
                  warning: value.fund_data[tableName][0]?.warning?.[key],
                  fieldOrder: columnLables[key]?.order
                })
              )
          : [
              ...(isComparisonReport
                ? [
                    getColumnByType({
                      type: schema?.types?.[key]?.type,
                      options: schema?.types?.[key]?.options,
                      key,
                      defaultValue: schema?.defaultValues?.[key],
                      index: 0,
                      value: funds[currentDataKey].previous_fund.fund_data[tableName][0],
                      required: requiredFields.includes(key),
                      tableName: tableName,
                      isStaticTemplate,
                      nonEditable: columnLables[key].disabled || isComparisonReport,
                      isMultiFunds,
                      isEsma: true,
                      warning:
                        funds[currentDataKey].previous_fund.fund_data[tableName][0]?.warning?.[key],
                      fundId: funds[currentDataKey].previous_fund.fund_id,
                      isSectionDisabled: isTableDisabledForFund(
                        funds[currentDataKey].previous_fund
                      ),
                      fundName: funds[currentDataKey].previous_fund.fund_name,
                      fieldOrder: columnLables[key]?.order
                    })
                  ]
                : []),
              getColumnByType({
                type: schema?.types?.[key]?.type,
                options: schema?.types?.[key]?.options,
                key,
                defaultValue: schema?.defaultValues?.[key],
                index: 0,
                value: data[0],
                required: requiredFields.includes(key),
                tableName: tableName,
                isStaticTemplate,
                nonEditable: columnLables[key].disabled || isComparisonReport,
                isMultiFunds,
                isEsma: true,
                warning: data?.[0]?.warning?.[key],
                fundId: data[0]?.fund_id,
                isSectionDisabled: isTableDisabledForFund(data[0]?.fund_id),
                fundName: data[0]?.fund_name,
                fieldOrder: columnLables[key]?.order,
                difference: isComparisonReport
                  ? funds[currentDataKey]?.difference?.[tableName]?.[0]?.[key] || {}
                  : null
              })
            ];

        // Each value column stacks its own value above its own error, so the
        // row must fit the tallest column — pairing the longest value with a
        // different column's longest error over-sizes it.
        const rows = isMultiFunds
          ? isComparisonReport
            ? Object.values(funds)
                .map(fund => [fund.previous_fund, fund])
                .flat()
                .map(fund => fund.fund_data[tableName][0])
            : Object.values(funds).map(fund => fund.fund_data[tableName][0])
          : isComparisonReport
            ? [funds[currentDataKey].previous_fund.fund_data[tableName][0], data[0]]
            : [data[0]];

        const rowHeight = getRowHeight({
          label: columnLables[key]?.name,
          labelWidth,
          cells: getRowCells(rows, key, schema?.types?.[key]?.options, false),
          valueWidth,
          hasWarning: !!data?.[0]?.warning?.[key]
        });

        return {
          rowId: isMultiFunds
            ? isComparisonReport
              ? index + 2
              : index + 1
            : isComparisonReport
              ? index + 1
              : index,
          height: rowHeight,
          cells: [
            {
              type: 'header',
              text: `${columnLables[key]?.order || ''}`
            },
            {
              // type: 'header',
              // text: columnLables[key]?.name || '',
              type:
                (!isMultiFunds &&
                  data[0]?.report_id &&
                  data[0]?.[key] !== null &&
                  schema.types[key]?.has_drill_down_feature) ||
                columnLables[key]?.name?.includes('*')
                  ? 'customHeader'
                  : 'header',
              text: columnLables[key]?.name || '',
              render: () => (
                <CustomHeaderWithDrillDown
                  title={columnLables[key]?.name}
                  reportId={data[0]?.report_id}
                  fundId={data[0].fund_id}
                  columnName={key}
                  tableName={tableName}
                  tableId={data[0]?.id}
                  hasDrillDown={!isMultiFunds && !!schema.types[key]?.has_drill_down_feature}
                />
              ),
              style: {
                'justify-content': 'flex-start',
                'white-space': 'inherit'
              }
            },
            ..._cells
          ]
        };
      })
    ];
  }, [data, isMultiFunds, isStaticTemplate, contentWidth]);

  const columns = useMemo(() => {
    const _fundSize = Object.keys(funds).length;
    const _columns = isMultiFunds
      ? _fundSize <= 3 && !isComparisonReport
        ? Array.from(Array(_fundSize + 1).keys()).map(index => ({
            columnId: index + 1,
            width: contentWidth / (_fundSize + 1)
          }))
        : isComparisonReport
          ? [
              {
                columnId: 1,
                width: 200
              },
              ...Object.values(funds)
                .map(item => [item, item.previous_fund])
                .flat()
                .map((_, index) => ({
                  columnId: index + 2,
                  width: 300
                }))
            ]
          : [
              {
                columnId: 1,
                width: 200
              },
              ...Object.keys(funds).map((_, index) => ({
                columnId: index + 2,
                width: 300
              }))
            ]
      : isComparisonReport
        ? Array.from(Array(3).keys()).map(index => ({
            columnId: index + 1,
            width: contentWidth / 3
          }))
        : Array.from(Array(2).keys()).map(index => ({
            columnId: index + 1,
            width: contentWidth / 2
          }));
    return [
      {
        columnId: 0,
        resizable: true,
        width: 30
      },
      ..._columns
    ];
  }, [isMultiFunds, funds, data, contentWidth, schema, isComparisonReport]);

  return { rows, columns, onChange };
}
